import React, { Fragment, useEffect } from 'react'
import { FixedSizeList } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import { useDispatch, useSelector } from 'react-redux'
import { useLogsActions } from 'core/actions/logs'
import { Syslog } from 'api/model/Syslog'
import { format } from 'date-fns'
import classNames from 'classnames'
import { AuditMessage } from 'api/model/AuditMessage'
import { saveAs } from 'file-saver'
import WebFont from 'webfontloader'
import { CopyOutlined } from '@ant-design/icons'

import './style.css'
import { useAppSelector } from '../../core/hooks'
import { getApi } from '../../core/api'
import { hostname } from 'os'
import Icon from '../../elements/Icon'
import { Button } from 'antd'

export interface LogsGridProps {
  width: number
  height: number
}

export const LogsGrid = ({ width, height }: LogsGridProps): JSX.Element => {
  const {
    api: { syslogApi },
    logs: { rows, waitingForSearchResults, bookmark, descriptor, tags },
  } = useAppSelector((state) => ({
    api: getApi(state.auth.user) ?? { syslogApi: null },
    logs: state.logs,
  }))

  const { loadNextPageOfSearchResults } = useLogsActions(useDispatch())

  useEffect(() => {
    WebFont.load({
      google: {
        api: 'css2',
        families: ['Open Sans Condensed'],
      },
    })
  }, [])
  const loadMoreItems = waitingForSearchResults
    ? // eslint-disable-next-line @typescript-eslint/no-empty-function
      async () => {}
    : async (start: number, stop: number) => {
        syslogApi &&
          (await loadNextPageOfSearchResults({
            api: syslogApi,
            descriptor: descriptor && Object.assign({}, descriptor, { limit: Math.max(descriptor?.limit || 100, stop - rows.length) }),
            tags: tags,
            bookmark: bookmark,
          }))
      }
  const isItemLoaded = (idx: number) => !bookmark || idx < rows.length

  const downloadJson = (name: string, am: AuditMessage) => {
    saveAs(new Blob([JSON.stringify(am, null, ' ')], { type: 'application/json' }), name)
  }

  const downloadXml = (name: string, m: string) => {
    saveAs(new Blob([m], { type: 'application/xml' }), name)
  }

  const jsonLink = (id: string) => `${syslogApi.host}/syslogs/json/${id}`
  const xmlLink = (id: string) => `${syslogApi.host}/syslogs/xml/${id}`

  const columns = [
    { key: 'ts', width: 120, value: (sl: Syslog) => (sl.date ? format(new Date(sl.date), 'yyyy/MM/dd HH:mm:ss.SSS') : '-') },
    { key: 'ip', width: 80, value: (sl: Syslog) => sl.remoteAddress || '-' },
    { key: 'event', width: 60, value: (sl: Syslog) => sl.auditMessage?.eventIdentification?.eventTypeCode?.find((c) => c.codeSystemName === 'IHE Transactions')?.code || '-' },
    {
      key: 'ssid',
      width: 220,
      value: (sl: Syslog) =>
        sl.auditMessage?.participantObjectIdentifications?.find((p) => p.participantObjectIDTypeCode?.originalText === 'submission set classificationNode')?.participantObjectID ||
        '-',
    },
    {
      key: 'patient',
      width: 220,
      value: (sl: Syslog) =>
        sl.auditMessage?.participantObjectIdentifications?.find((p) => p.participantObjectIDTypeCode?.originalText === 'Patient Number')?.participantObjectID || '-',
    },
    {
      key: 'json',
      width: 50,
      value: (sl: Syslog) => (
        <div style={{ display: 'flex' }}>
          <span className={'btn'} style={{ display: 'flex' }} onClick={() => sl.auditMessage && downloadJson(`${sl.date}_${sl.id}.json`, sl.auditMessage)}>
            json
          </span>
          <span className={'btn'} style={{ display: 'flex' }} onClick={() => sl.id && navigator.clipboard.writeText(jsonLink(sl.id))}>
            <CopyOutlined />
          </span>
        </div>
      ),
    },
    {
      key: 'xml',
      width: 50,
      value: (sl: Syslog) => (
        <div style={{ display: 'flex' }}>
          <span className={'btn'} style={{ display: 'flex' }} onClick={() => sl.rawMessage && downloadXml(`${sl.date}_${sl.id}.xml`, sl.rawMessage)}>
            xml
          </span>
          <span className={'btn'} style={{ display: 'flex' }} onClick={() => sl.id && navigator.clipboard.writeText(xmlLink(sl.id))}>
            <CopyOutlined />
          </span>
        </div>
      ),
    },
  ]

  const itemCount = !!bookmark && bookmark != 'nil' && rows.length >= (descriptor?.limit || 100) ? rows.length + 1 : rows.length
  const totWidth = columns.reduce((acc, c) => acc + c.width, 0)
  return (
    <React.Fragment>
      <div className={'row'} style={{ width: `${itemCount * 24 > height ? width - 4 : width}px` }}>
        {columns.map((c) => (
          <div className={classNames('cell', 'heading')} key={c.key} style={{ width: `calc(${(100.0 * c.width) / totWidth}% - ${Math.ceil(0 / columns.length)}px` }}>
            {c.key}
          </div>
        ))}
      </div>
      <InfiniteLoader isItemLoaded={isItemLoaded} itemCount={itemCount} threshold={15} loadMoreItems={loadMoreItems}>
        {({ onItemsRendered, ref }) => (
          <FixedSizeList className={'list'} itemCount={itemCount} onItemsRendered={onItemsRendered} ref={ref} itemSize={16} height={height} width={width}>
            {({ index, style: itemStyle }) => {
              return (
                <div className={'row'} style={itemStyle}>
                  {!isItemLoaded(index)
                    ? 'Loading...'
                    : columns.map((c) => {
                        return (
                          <div className={'cell'} key={c.key} style={{ width: `calc(${(100.0 * c.width) / totWidth}% - ${Math.ceil(0 / columns.length)}px` }}>
                            {c.value(rows[index])}
                          </div>
                        )
                      })}
                </div>
              )
            }}
          </FixedSizeList>
        )}
      </InfiniteLoader>
    </React.Fragment>
  )
}
